// dashboard/chat/index.tsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Form, Input, UncontrolledTooltip } from "reactstrap";
import { Link } from "react-router-dom";

// components
import AppSimpleBar from "../../../components/AppSimpleBar";
import AddGroupModal from "../../../components/AddGroupModal";
import InviteContactModal from "../../../components/InviteContactModal";
import AddButton from "../../../components/AddButton";
import ContactModal from "../../../components/ContactModal";

import Favourites from "./Favourites";
import DirectMessages from "./DirectMessages";
import Archive from "./Archive";
import { CHATS_TABS } from "../../../constants";

// interfaces
import { useAuth } from "../../../context/AuthContext";
import { IUserTypes, IContact, mapContactToUserTypes, IRegion, regionList, tagListHuman } from "../../../types/contacts";

// hook
import { useChats } from "../../../hooks/useChats";
import ChatsNoAssigned from "./ChatsNoAssigned";
import { useMessages } from "../../../context/MessagesContext";
import { useChat } from "../../../context/ChatContext";
import { useContacts } from "../../../hooks/useContacts";
import SelectRegion from "./SelectRegion";
import { ChatTags } from "./ChatTags";
import Loader from "../../../components/Loader";
import { toTimestampSafe } from "../../../utils";

const Index = () => {
  /** ------------------- STATES ------------------- **/
  const [activeTab, setActiveTab] = useState(CHATS_TABS.DEFAULT);
  const [isOpenInvite, setIsOpenInvite] = useState(false);
  const [isOpenAddContact, setIsOpenAddContact] = useState(false);
  const [isOpenCreateChannel, setIsOpenCreateChannel] = useState(false);
  /** ------------------- FETCH FAVORITES/CONTACTS ------------------- **/
  const { sessions, contactNoAssigned, favourites, contactMarked, contactBlocked, loadContactsMarked, loadContactsBlocked, loadFavourites } = useMessages();

  const [searchTerm, setSearchTerm] = useState("");

  const [noAssignedContacts, setNoAssignedContacts] = useState<IUserTypes[]>([]);
  const [directActive, setDirectActive] = useState<IUserTypes[]>([]);
  const [directDoubts, setDirectDoubts] = useState<IUserTypes[]>([]);

  const [selectedTagId, setSelectedTagId] = useState<number>(0);
  const [selectedRegionId, setSelectedRegionId] = useState<number>(-1);


  const { isAppReady, selectedChatId, handleSelectChat, chatUserDetails } = useChat();
  const { contactsMap, contactsByPhoneMap, addContact } = useContacts();

  const { user } = useAuth();
  const { chatListContactMenu } = useChats();

  /** ------------------- MODAL HANDLERS ------------------- **/
  const openInviteModal = () => setIsOpenInvite(true);
  const closeInviteModal = () => setIsOpenInvite(false);

  const openAddContactModal = () => setIsOpenAddContact(true);
  const closeAddContactModal = () => setIsOpenAddContact(false);

  const openCreateChannelModal = () => setIsOpenCreateChannel(true);
  const closeCreateChannelModal = () => setIsOpenCreateChannel(false);



  const filterBySearch = useCallback((users: IUserTypes[]) => {

    if (!searchTerm.trim()) return users;

    const term = searchTerm.toLowerCase();

    return users.filter(u =>
      (u.name || "").toLowerCase().includes(term) ||
      (u.fullName || "").toLowerCase().includes(term) ||
      (u.phoneNumber || "").toLowerCase().includes(term)
    );

  }, [searchTerm]);

  const applyFilters = useCallback((users: IUserTypes[]) => {

    let filtered = users;

    // 🔍 busca
    filtered = filterBySearch(filtered);

    // 🌎 região
    // if (selectedRegionId !== 0) {
    if (selectedRegionId >= 0) {
      filtered = filtered.filter(user => {
        const contact = contactsMap.get(user.id);
        return contact?.idRegiao === selectedRegionId;
      });
    }

    return filtered;

  }, [selectedTagId, selectedRegionId, contactsMap, filterBySearch]);

  const filteredMarked = React.useMemo(() => {
    const filtered = applyFilters(contactMarked);

    return [...filtered].sort(
      (a, b) =>
        toTimestampSafe(b.lastMessageTime) -
        toTimestampSafe(a.lastMessageTime)
    );
  }, [contactMarked, applyFilters]);

  const filteredBlocked = React.useMemo(() => {
    const filtered = applyFilters(contactBlocked);
    // console.log(contactBlocked);
    return [...filtered].sort(
      (a, b) =>
        toTimestampSafe(b.lastMessageTime) -
        toTimestampSafe(a.lastMessageTime)
    );
  }, [contactBlocked, applyFilters]);

  const filteredDirectActive = React.useMemo(() => {
    const filtered = applyFilters(directActive);
    return filtered.sort((a, b) => toTimestampSafe(b.lastMessageTime) - toTimestampSafe(a.lastMessageTime));
  }, [directActive, applyFilters]);

  const filteredDirectDoubt = React.useMemo(() => {
    const filtered = applyFilters(directDoubts);
    return filtered.sort((a, b) => toTimestampSafe(b.lastMessageTime) - toTimestampSafe(a.lastMessageTime));
  }, [directDoubts, applyFilters]);

  // const filteredDirectFinish = React.useMemo(() => {
  //   const filtered = applyFilters(directFinish);
  //   return filtered.sort((a, b) => toTimestampSafe(b.lastMessageTime) - toTimestampSafe(a.lastMessageTime));
  // }, [directFinish, applyFilters]);

  const filteredNoAssigned = React.useMemo(() => {
    const filtered = applyFilters(noAssignedContacts);
    return filtered.sort((a, b) => toTimestampSafe(b.lastMessageTime) - toTimestampSafe(a.lastMessageTime));
  }, [noAssignedContacts, applyFilters]);

  const filteredFavourites = React.useMemo(() => {
    const filtered = applyFilters(favourites);
    return filtered.sort((a, b) => toTimestampSafe(b.lastMessageTime) - toTimestampSafe(a.lastMessageTime));
  }, [favourites, applyFilters]);


  const onAddContactMessage = async (contactsAddMessage: Array<string | number>) => {
    // console.log('onContactMessage', contactsAddMessage)

    if (contactsAddMessage.length === 0)
      return

    const contact = contactsMap.get(contactsAddMessage[0] as number);
    if (!contact) return;

    closeAddContactModal();

    //Seleciona o Chat
    handleSelectChat(contact.id)

    // if (contact) {
    //   showSuccessNotification("Contato selecionado com sucesso!");
    // } else {
    //   showErrorNotification("Erro ao enviar Contato!")
    // }
  };

  const onInviteContactMessage = async (data: any) => {

    if (!user?.idEnterprise) {
      console.log('idEnterprise', user?.idEnterprise)
      return;
    }

    let contact = contactsByPhoneMap.get(data.phone);
    //caso ja exista somente seleciona
    if (contact) {
      // console.log('[onInviteContactMessage] ja existe', contact)
      //Seleciona o Chat      
      handleSelectChat(contact.id)
      closeInviteModal();
      return;
    }

    //Cadasta novo contato
    const contactNew: IContact = {
      id: 0,
      idCliente: data.idCliente,
      cpf: data.cpf,
      name: data.name,
      phoneNumber: data.phone,
      email: "",
      unreadCount: 0,
      avatar: "",
      //blockedMessage: "",
      isGroup: false,
      robot: false,
      lastMessageText: "",
      //lastMessageTime: "",
      members: 0,
      idEnterprise: user?.idEnterprise,
    };

    const newContact = await addContact(contactNew)
    // console.log('[onInviteContactMessage] newContact', newContact)
    if (!newContact) {
      // exibir erro ou impedir envio
      return;
    }

    // setPendingSelectChatId(newContact.id);
    //const resultContacts = await getContacts();

    //Seleciona o Chat
    // console.log('[onInviteContactMessage] Seleciona o Chat', newContact.id)
    handleSelectChat(newContact.id)

    // console.log('onSendMessage', params)
    closeInviteModal();
  };

  //Pega os dados context, sinalR
  useEffect(() => {
    let ignore = false;

    const fetchBaseData = async () => {
      if (!user?.id || !user.idEnterprise) return;

      try {
        await Promise.all([
          loadFavourites(),
          loadContactsMarked(),
          loadContactsBlocked(),
          // loadContactAssigned(),
          // loadSessionsActive(),
        ]);

        if (ignore) return;

      } catch (error) {
        console.error("[Leftbar] Erro ao buscar dados:", error);
      }
    };

    fetchBaseData();

    return () => {
      ignore = true;
    };
  }, [user?.id, user?.idEnterprise]);


  //2- Trata da exibição das conversas no menu (HUMANO)
  useEffect(() => {
    if (!chatListContactMenu?.length || !user?.id) {
      return;
    }

    const userId = Number(user.id);
    const idUserMain = 3;//  Number(sess.idUserMain);
    const idUserIA = 1; //Number(user.idUserIA);

    // 🧠 Monta MAPA com a ÚLTIMA sessão por contato
    const sessionMap = new Map<number, any>();

    sessions.forEach(s => {
      const contactId = Number(s.idContact);
      const existing = sessionMap.get(contactId);

      if (!existing || new Date(s.createdAt) > new Date(existing.createdAt)) {
        sessionMap.set(contactId, s);
      }
    });

    // 🟡 Sem atribuição
    const noAssignedIds = new Set(
      contactNoAssigned.map(c => Number(c.id))
    );

    //LOG 🟡 DÚVIDAS (veio do robô)
    // sessions.forEach(s => {
    //   console.log({
    //     isActive: s.isActive,
    //     aiCurrentStep: s.aiCurrentStep,
    //     aiFallbackToHuman: s.aiFallbackToHuman
    //   });
    // });

    // 🟡 DÚVIDAS (veio do robô)
    const doubtSessionContacts = new Set(
      sessions
        .filter(s => {
          // const active = Number(s.isActive === 1);
          const active = s.isActive === true;
          const step = Number(s.aiCurrentStep);
          const fallback = Number(s.aiFallbackToHuman) === 1;

          return active && fallback && step === 103;
        })
        .map(s => Number(s.idContact))
    );

    // console.log('doubtSessionContacts', doubtSessionContacts)

    // 🟢 NOVOS (fila)
    const noAssignedActive = contactNoAssigned
      .filter(c => {
        const session = sessionMap.get(Number(c.id));
        if (!session) return false;

        // 🚫 NÃO pode estar na lista de dúvidas
        if (doubtSessionContacts.has(Number(c.id))) {
          return false;
        }

        return (
          Number(session.idUser) === idUserMain // 🟡 fila
        );
      })
      .sort((a, b) =>
        toTimestampSafe(b.lastMessageTime) - toTimestampSafe(a.lastMessageTime)
      );

    /**
     * 🧠 REGRA CENTRAL (AGORA CORRETA)
     */
    const direct = chatListContactMenu.filter(chat => {
      const id = Number(chat.id);
      const session = sessionMap.get(id);

      if (!session) return false;

      const sessionUserId = Number(session.idUser);

      // 🚫 REMOVE IA
      if (sessionUserId === idUserIA) return false;

      // ✅ SOMENTE:
      // - minhas
      // - fila (main)
      return (
        sessionUserId === userId ||
        sessionUserId === idUserMain
      );
    });

    // 🟢 ATIVOS (MINHAS)
    const directActive = direct
      .filter(c => {
        const session = sessionMap.get(Number(c.id));
        if (!session) return false;

        return (
          Number(session.idUser) === userId &&
          session.isActive
        );
      })
      .sort((a, b) =>
        toTimestampSafe(b.lastMessageTime) - toTimestampSafe(a.lastMessageTime)
      );

    // 🟡 DÚVIDAS
    const directDoubts = direct
      .filter(c => doubtSessionContacts.has(Number(c.id)))
      .sort((a, b) =>
        toTimestampSafe(b.lastMessageTime) - toTimestampSafe(a.lastMessageTime)
      );

    // 🔥 UPDATE FINAL
    setNoAssignedContacts(noAssignedActive);
    setDirectActive(directActive);
    setDirectDoubts(directDoubts);

  }, [
    chatListContactMenu,
    sessions,
    contactNoAssigned,
    favourites,
    contactMarked,
    user?.id,
  ]);




  // useEffect(() => {
  //   if (!chatListContactMenu?.length || !user?.id) {
  //     return;
  //   }

  //   const sessionMap = new Map<number, any>();

  //   sessions.forEach(s => {
  //     const contactId = Number(s.idContact);

  //     const existing = sessionMap.get(contactId);

  //     if (!existing) {
  //       sessionMap.set(contactId, s);
  //       return;
  //     }

  //     // 👉 pega a mais recente
  //     if (new Date(s.createdAt) > new Date(existing.createdAt)) {
  //       sessionMap.set(contactId, s);
  //     }
  //   });


  //   // 🔧 HELPERS (padroniza regra e evita erro no futuro)
  //   const isHuman = (s: any) =>
  //     Number(s.isAiActive) === 0;

  //   const isRobot = (s: any) =>
  //     Number(s.isAiActive) === 1;

  //   const isFallback = (s: any) =>
  //     Number(s.aiFallbackToHuman) === 1 || s.aiFallbackToHuman === true;

  //   // 🟡 Sem atribuição (FONTE OFICIAL)
  //   const noAssignedIds = new Set(
  //     contactNoAssigned.map(c => Number(c.id))
  //   );

  //   // 🟡 DÚVIDAS (veio do robô → humano)
  //   const doubtSessionContacts = new Set(
  //     sessions
  //       .filter(s =>
  //         isHuman(s) &&
  //         isFallback(s) &&
  //         Number(s.aiCurrentStep) === 103
  //       )
  //       .map(s => Number(s.idContact))
  //   );

  //   // 🟢 Sessões ATIVAS (NÃO minhas, NÃO IA)
  //   const otherActiveSessionContacts = new Set(
  //     sessions
  //       .filter(s =>
  //         isHuman(s) &&
  //         s.isActive &&
  //         Number(s.idUser) !== Number(user.id)
  //       )
  //       .map(s => Number(s.idContact))
  //   );

  //   // 🟢 Sessões ATIVAS (MINHAS)
  //   const myActiveSessionContacts = new Set(
  //     sessions
  //       .filter(s =>
  //         isHuman(s) &&
  //         s.isActive &&
  //         Number(s.idUser) === Number(user.id)
  //       )
  //       .map(s => Number(s.idContact))
  //   );

  //   // ⛔ Sessões ATIVAS de OUTROS usuários
  //   const foreignActiveContacts = new Set(
  //     sessions
  //       .filter(s =>
  //         isHuman(s) &&
  //         s.isActive &&
  //         Number(s.idUser) !== Number(user.id)
  //       )
  //       .map(s => Number(s.idContact))
  //   );

  //   // 🟡 NOVOS (fila / não atribuídos)
  //   const noAssignedActive = contactNoAssigned
  //     .filter(c => otherActiveSessionContacts.has(Number(c.id)))
  //     .sort(
  //       (a, b) =>
  //         toTimestampSafe(b.lastMessageTime) -
  //         toTimestampSafe(a.lastMessageTime)
  //     );

  //   /**
  //    * 🧠 REGRA CENTRAL:
  //    * - NÃO pode ser IA
  //    * - NÃO pode estar sem atribuição
  //    * - NÃO pode estar com outro atendente
  //    */
  //   // const direct = chatListContactMenu.filter(chat => {
  //   //   const id = Number(chat.id);

  //   //   const session = sessions.find(s => Number(s.idContact) === id);

  //   //   // 🚫 remove qualquer sessão da IA
  //   //   if (session && isRobot(session)) return false;

  //   //   return (
  //   //     !noAssignedIds.has(id) &&
  //   //     !foreignActiveContacts.has(id)
  //   //   );
  //   // });

  //   const direct = chatListContactMenu.filter(chat => {
  //     const id = Number(chat.id);

  //     const session = sessionMap.get(id);

  //     // 🚫 remove qualquer sessão da IA (AGORA CORRETO)
  //     if (session && Number(session.isAiActive) === 1) return false;

  //     return (
  //       !noAssignedIds.has(id) &&
  //       !foreignActiveContacts.has(id)
  //     );
  //   });


  //   // 🟢 ATIVOS (MEUS)
  //   const directActive = direct
  //     .filter(c => myActiveSessionContacts.has(Number(c.id)))
  //     .sort(
  //       (a, b) =>
  //         toTimestampSafe(b.lastMessageTime) -
  //         toTimestampSafe(a.lastMessageTime)
  //     );

  //   // 🟡 DÚVIDAS (vindas do robô)
  //   const directDoubts = direct
  //     .filter(c => doubtSessionContacts.has(Number(c.id)))
  //     .sort(
  //       (a, b) =>
  //         toTimestampSafe(b.lastMessageTime) -
  //         toTimestampSafe(a.lastMessageTime)
  //     );

  //   // 🔥 Atualiza estado final
  //   setNoAssignedContacts(noAssignedActive);
  //   setDirectActive(directActive);
  //   setDirectDoubts(directDoubts);

  // }, [
  //   chatListContactMenu, // 🔥 ESSENCIAL (SignalR)
  //   sessions,            // 🔥 ESSENCIAL
  //   contactNoAssigned,
  //   favourites,
  //   contactMarked,
  //   user?.id,
  // ]);

  const showAll = selectedTagId === 0;
  const showNew = selectedTagId === 1;
  const showActive = selectedTagId === 2;
  const showFinished = selectedTagId === 3;
  const showMarked = selectedTagId === 4;
  const showBlocked = selectedTagId === 6
  const showFavourites = selectedTagId === 5;
  const showQuestion = selectedTagId === 7;


  /** ------------------- RENDER ------------------- **/
  return (
    <>
      <div>
        <div className="px-2 pt-2">
          <div className="d-flex align-items-start">
            <div className="flex-grow-1">
              <h4 className="mb-4">👤 Mensagens</h4>
            </div>
            <div className="flex-shrink-0">
              <div id="add-contact">
                <AddButton id="add-contact" onClick={openInviteModal} />
              </div>
              <UncontrolledTooltip target="add-contact" placement="bottom"
                transition={{ timeout: 1 }}
                delay={0}
              >
                Novo Contato
              </UncontrolledTooltip>
            </div>
          </div>

          <Form>
            <div className="input-group">
              <SelectRegion
                selectedRegionId={selectedRegionId}
                regions={regionList}
                onSelectRegion={(id) => setSelectedRegionId(Number(id))}
              />
            </div>

            <div className="input-group mb-2">

              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                type="text"
                className="form-control bg-light border-0 pe-0"
                placeholder="pesquisar aqui.."
              />
              <Button color="light" type="button" id="searchbtn-addon">
                <i className="bx bx-search align-middle"></i>
              </Button>
            </div>
            <div className="input-group mb-3">
              <ChatTags
                tags={tagListHuman}
                value={selectedTagId}
                onChange={(id) => setSelectedTagId(id)}
              />
            </div>


          </Form>
        </div>{" "}

        {!isAppReady && <Loader />}

        <AppSimpleBar className="chat-room-list">
          {activeTab === CHATS_TABS.DEFAULT && (
            <>
              {showFavourites && filteredFavourites.length > 0 && (
                <Favourites
                  users={filteredFavourites}
                  selectedChatId={selectedChatId as number}
                  onSelectChat={handleSelectChat}
                />
              )}

              {/* Conversas Duvidas  */}
              {(showAll || showQuestion) && filteredDirectDoubt.length > 0 && (
                <DirectMessages
                  key="humanMessagesDoubts"
                  title="Conversas com dúvidas"
                  users={filteredDirectDoubt}
                  openAddContact={() => null}
                  selectedChatId={selectedChatId as number}
                  onSelectChat={handleSelectChat}
                />
              )}

              {(showAll || showNew) && filteredNoAssigned.length > 0 && (
                <ChatsNoAssigned
                  users={filteredNoAssigned}
                  selectedChatId={selectedChatId as number}
                  onSelectChat={handleSelectChat}
                />
              )}

              {/* <h5 className="text-Left">
                <Link
                  to="#"
                  className="mb-3 px-4 mt-4 font-size-11 text-primary"
                  onClick={() => setActiveTab(CHATS_TABS.ARCHIVE)}
                >
                  Conversas Finalizadas <i className="bx bxs-archive-in align-middle" />
                </Link>
              </h5> */}



              {/* Conversas ativas  */}
              {(showAll || showActive) && filteredDirectActive.length > 0 && (
                <DirectMessages
                  key="msgActive"
                  title="Conversas ativas"
                  users={filteredDirectActive}
                  openAddContact={openAddContactModal}
                  selectedChatId={selectedChatId as number}
                  onSelectChat={handleSelectChat}
                />
              )}

              {/* Contatos marcado  */}
              {(showMarked) && filteredMarked.length > 0 && (
                <DirectMessages
                  key="msgMarked"
                  title="Conversas Marcadas"
                  users={filteredMarked}
                  openAddContact={openAddContactModal}
                  selectedChatId={selectedChatId as number}
                  onSelectChat={handleSelectChat}
                />
              )}

              {/* Contatos bloqueado  */}
              {(showBlocked) && filteredBlocked.length > 0 && (
                <DirectMessages
                  key="msgBloqued"
                  title="Conversas Bloqueadas"
                  users={filteredBlocked}
                  openAddContact={openAddContactModal}
                  selectedChatId={selectedChatId as number}
                  onSelectChat={handleSelectChat}
                />
              )}

              {/* Finalizadas   */}
              {/* {showFinished && filteredDirectFinish.length > 0 && (
                <Archive
                  users={filteredDirectFinish}
                  selectedChatId={selectedChatId as number}
                  onSelectChat={handleSelectChat}
                />
              )} */}

              {/* <h5 className="text-Left">
                <Link
                  to="#"
                  className="mb-3 px-4 mt-4 font-size-11 text-primary"
                  onClick={() => setActiveTab(CHATS_TABS.ARCHIVE)}
                >
                  Conversas Finalizadas <i className="bx bxs-archive-in align-middle" />
                </Link>
              </h5> */}
            </>
          )}

          {activeTab === CHATS_TABS.ARCHIVE && (
            <>
              {/* <Archive archiveContacts={archiveContacts} /> */}

              {/* <h5 className="text-Left">
                <Link
                  to="#"
                  className="mb-3 px-4 mt-4 font-size-11 text-primary"
                  onClick={() => setActiveTab(CHATS_TABS.DEFAULT)}
                >
                  Mensagens Ativas <i className="bx bxs-archive-out align-middle" />
                </Link>
              </h5> */}

              {/* {(showAll || showFinished) && filteredDirectFinish.length > 0 && (
                <Archive users={filteredDirectFinish} />
              )} */}
            </>
          )}
        </AppSimpleBar>
      </div>

      {isOpenCreateChannel && (
        <AddGroupModal
          isOpen={isOpenCreateChannel}
          onClose={closeCreateChannelModal}
          onCreateChannel={openCreateChannelModal}
        />
      )}

      {isOpenInvite && (
        <InviteContactModal
          isOpen={isOpenInvite}
          onClose={closeInviteModal}
          onInvite={onInviteContactMessage}
        />
      )}

      {isOpenAddContact && (
        <ContactModal
          isOpen={isOpenAddContact}
          onClose={closeAddContactModal}
          onAddContact={onAddContactMessage}
        />
      )}
    </>
  );
};

export default Index;
